import { useContext, useEffect, useMemo, useState } from 'react'
import { Form } from 'antd'
import {
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  QuestionnaireResponseItemAnswer
} from '@medplum/fhirtypes'
import { isEqual } from 'lodash'
import { QuestionnaireAnswerType } from 'components/Assessments/assessmentType'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import AssessmentFormContext from 'contexts/AssessmentFormContext'
import { ValidateFormValuesType } from './useValidateAndUpdateFormValues'
import { getSectionDependentQuestionValues } from '../helpers'
import { PATQuestionnaireSections, SECTION_QUESTION_FIELDS_MAP } from '../constants'
import { SectionDependentItems } from '../types/questionnaire'
import { ScoreAndSummaryFormField } from '../sections/ScoreAndSummary/formField'

const ITEMS_NEEDING_LIVE_UPDATES: string[] = [
  SectionDependentItems.BRADEN_SCALE,
  SectionDependentItems.CNA_SKILLED_TASKS,
  SectionDependentItems.MODIFIER_A,
  SectionDependentItems.MODIFIER_B,
  SectionDependentItems.QUALIFICATION_STATUS
]

const getNeedsLiveUpdates = (questionLinkId: string): boolean => {
  return ITEMS_NEEDING_LIVE_UPDATES.includes(questionLinkId)
}

export interface GetAnswerFromSectionResultsProps {
  questionType?: QuestionnaireAnswerType
  questionLinkId: string
  questionText?: string
  questionAnswerOptions?: QuestionnaireItem['answerOption']
  questionInitialSelected?: QuestionnaireItemAnswerOption
}

interface GetAnswerFromSectionResults {
  generatedItemValue:
    | ValidateFormValuesType
    | string
    | string[]
    | null
    | undefined
    | Record<string, QuestionnaireResponseItemAnswer>
  isSectionForItemComplete: boolean | null
}

export const useGenerateAnswerFromSectionResults = ({
  questionType,
  questionLinkId,
  questionText,
  questionAnswerOptions,
  questionInitialSelected
}: GetAnswerFromSectionResultsProps): GetAnswerFromSectionResults => {
  const { answers, response } = useAssessmentContext()
  const { form } = useContext(AssessmentFormContext)
  const [generatedItemValue, setGeneratedItemValue] = useState<
    ValidateFormValuesType | string | string[] | null
  >(null)
  const [_, setTargetAnswerList] = useState<
    Record<string, QuestionnaireResponseItemAnswer> | string[]
  >({})

  // Watch entire form
  const allFormValues = Form.useWatch([], form)

  // Extract only the fields needed for the current questionLinkId
  const formValues = useMemo(() => {
    if (!allFormValues) return undefined

    if (questionLinkId === SectionDependentItems.QUALIFICATION_STATUS) {
      const totalFormValue = allFormValues[ScoreAndSummaryFormField.SCORE_AND_SUMMARY_TOTAL]
      if (totalFormValue !== undefined) {
        return {
          [ScoreAndSummaryFormField.SCORE_AND_SUMMARY_TOTAL]: totalFormValue
        }
      }
      return undefined
    }

    // Map questionLinkId to section name
    let sectionName: string | undefined
    if (questionLinkId === SectionDependentItems.SKILLED_NEEDS_LIST) {
      sectionName = PATQuestionnaireSections.CNASkilledTasks
    } else if (questionLinkId === SectionDependentItems.BRADEN_SCALE) {
      sectionName = PATQuestionnaireSections.BradenScale
    } else if (questionLinkId === SectionDependentItems.CNA_SKILLED_TASKS) {
      sectionName = PATQuestionnaireSections.CNASkilledTasks
    } else {
      sectionName = questionLinkId.split('/')[1]
    }

    const questionFields = SECTION_QUESTION_FIELDS_MAP[sectionName]

    if (!questionFields) {
      return undefined
    }

    // Extract only the fields we need from all form values
    const needsLiveUpdates = getNeedsLiveUpdates(questionLinkId)

    const values: Record<string, unknown> = {}
    questionFields.forEach(({ formField, linkId }) => {
      const value = allFormValues[formField] ?? (linkId ? allFormValues[linkId] : undefined)
      if (value !== undefined) {
        values[formField] = value
      }
    })

    if (needsLiveUpdates) {
      return values
    }
    return Object.keys(values).length > 0 ? values : undefined
  }, [allFormValues, questionLinkId])

  const sectionDependentValues = useMemo(() => {
    const result = getSectionDependentQuestionValues({
      questionLinkId,
      questionAnswerOptions,
      questionInitialSelected,
      answers,
      response,
      formValues
    })
    return result
  }, [
    answers,
    questionAnswerOptions,
    questionInitialSelected,
    response,
    questionLinkId,
    formValues
  ])

  // For sections that depend on form values, create a hash to track changes
  const formValuesHash = useMemo(() => {
    const needsLiveUpdates = getNeedsLiveUpdates(questionLinkId)
    if (needsLiveUpdates && formValues !== undefined) {
      return JSON.stringify(formValues)
    }
    return null
  }, [formValues, questionLinkId])
  // The section of the answer list that the question is dependent on
  const targetAnswerSection = sectionDependentValues?.currentAnswerList
  const isSectionForItemComplete = sectionDependentValues?.isSectionForItemComplete ?? null

  useEffect(() => {
    if (sectionDependentValues) {
      const needsLiveUpdates = getNeedsLiveUpdates(questionLinkId)

      const shouldProcess = needsLiveUpdates
        ? formValues !== undefined
        : targetAnswerSection !== undefined

      if (shouldProcess) {
        const shouldUpdateValue = needsLiveUpdates || targetAnswerSection

        if (shouldUpdateValue) {
          // if itemValue was generated, set the generated item value
          if (
            sectionDependentValues.itemValue !== null &&
            sectionDependentValues.itemValue !== undefined &&
            questionText &&
            questionType
          ) {
            const newValue = {
              linkId: questionLinkId,
              text: questionText,
              type: questionType,
              value: sectionDependentValues.itemValue
            }
            setGeneratedItemValue((prevValue) => {
              if (!isEqual(prevValue, newValue)) {
                return newValue
              }
              return prevValue
            })
          }
          // if displayValue was generated, set the generated item value
          else if (sectionDependentValues.displayValue) {
            const newDisplayValue = sectionDependentValues.displayValue
            if (newDisplayValue) {
              setGeneratedItemValue((prevValue) => {
                if (!isEqual(prevValue, newDisplayValue)) {
                  return newDisplayValue
                }
                return prevValue
              })
            }
          }
        }

        // Update targetAnswerList tracking
        setTargetAnswerList((prevList) => {
          if (targetAnswerSection && !isEqual(prevList, targetAnswerSection)) {
            return targetAnswerSection
          }
          return prevList
        })
      }
    }
  }, [
    questionType,
    questionLinkId,
    questionText,
    questionAnswerOptions,
    questionInitialSelected,
    sectionDependentValues,
    targetAnswerSection,
    formValuesHash,
    formValues
  ])

  return { generatedItemValue, isSectionForItemComplete }
}
